import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { overlay } from 'overlay-kit';
import './events.quiz-share';

// enter_space·enter_play 는 이번 범위 밖의 기존 raw 이벤트라 그대로 둔다.
import { logger } from '@zep/core-logger';
import { trackEvent } from '@zep/logger/tracking';
import { useLocale, useViewport } from '@/hooks';
import { useUser } from '@/lib/auth';
import { useCanCreateClassroom } from '@/shared/hooks';
import { Button, DialogBody, DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { cn } from '@/utils/tailwind.util';
import { LinkContentToClassroomSection } from './LinkContentToClassroomSection';
import { ShareContent } from './ShareContent';
interface ContentShareModalCommonProps {
  title: string;
  /**
   * 공유 수단 이벤트(`quiz_share_method_click`)의 `place`. 같은 모달을 여러 화면이
   * 쓰므로 출처를 구분한다. 생략하면 `trackLocation` 값을 그대로 쓴다.
   */
  trackPlace?: string;
  /** 학급 맥락에서 열렸다면 그 학급. 진입·공유 수단 이벤트를 학급으로 잇는다. */
  classroomId?: string;
  contentId: string;
  contentOwnerId: string;
  shareUrl: string;
  hideFooter?: boolean;
  /**
   * 클래스 상세 페이지처럼 이미 특정 클래스 컨텍스트에 있는 곳에서 열린 공유 모달은
   * 클래스 연결 UI 를 노출하지 않는다.
   */
  hideClassroomLinking?: boolean;
  onClose: () => void;
}
interface QuizContentShareModalProps extends ContentShareModalCommonProps {
  contentType: 'QUIZSET';
  enterCode: number;
  spaceHashId: string;
}
interface BoardContentShareModalProps extends ContentShareModalCommonProps {
  contentType: 'BOARD';
  enterCode: number;
  spaceHashId?: never;
}
export type ContentShareModalProps = QuizContentShareModalProps | BoardContentShareModalProps;

/** 진입 이벤트(`enter_share`)에 얹을 문맥. 아는 화면만 채운다. */
type ShareTrackingContext = {
  trackLocation: string;
  isOwner?: boolean;
};
type ShowShareModalPayload = (Omit<QuizContentShareModalProps, 'onClose'> & ShareTrackingContext) | (Omit<BoardContentShareModalProps, 'onClose'> & ShareTrackingContext);
export function ContentShareModal(props: ContentShareModalProps) {
  const {
    locale
  } = useLocale();
  const {
    isMobile
  } = useViewport();
  const {
    t
  } = useTranslation(['common']);
  // 학생/로그아웃 사용자는 학급 목록도 만들기 버튼도 렌더되지 않는다. 그때도
  // 이 값이 true 면 모달이 커지고 오른쪽에 빈 198px 패널만 남으며 퀴즈 진입
  // 버튼까지 사라진다. 섹션 내부 노출 조건(useCanCreateClassroom)과 맞춘다.
  const canCreateClassroom = useCanCreateClassroom();
  // 제작자와 학생·타인이 같은 모달을 쓴다. 소유 여부 없이는 공유 수단별 선생님
  // 행동을 가를 수 없다. 판정은 레포 선례와 같은 축(quizUserId ↔ ownerId)을 쓴다.
  const {
    user
  } = useUser();
  // 학급 공유는 contentOwnerId 가 없을 때 빈 문자열을 넘긴다
  // (classroomContentActions.ts:53,70). 한쪽이라도 비면 비교 결과가 false 가 되어
  // 판정 불가인 공유가 전부 "남의 콘텐츠"로 집계된다.
  const isOwner = user?.quizUserId && props.contentOwnerId ? props.contentOwnerId === user.quizUserId : undefined;
  const canShowLinkContentToClassroom = !props.hideClassroomLinking && canCreateClassroom;
  return <ResponsiveDialog size={isMobile || canShowLinkContentToClassroom ? 'lg' : 'md'} onClose={props.onClose} className={canShowLinkContentToClassroom ? 'mobile-lg:!h-[583px]' : undefined} data-sentry-element="ResponsiveDialog" data-sentry-component="ContentShareModal" data-sentry-source-file="ContentShareModal.tsx">
      <DialogHeader title={t('shareModal.title')} hasCloseButton={true} onClose={props.onClose} data-sentry-element="DialogHeader" data-sentry-source-file="ContentShareModal.tsx" />
      <DialogBody className={cn('flex min-h-0 flex-col items-center gap-md overflow-auto px-md mobile-lg:px-lg', canShowLinkContentToClassroom && 'overflow-hidden mobile-lg:min-h-0 mobile-lg:flex-1 mobile-lg:flex-row mobile-lg:items-stretch mobile-lg:gap-lg')} data-sentry-element="DialogBody" data-sentry-source-file="ContentShareModal.tsx">
        <div className={cn('relative z-0 flex w-full min-w-0 flex-col items-center gap-md pb-lg', canShowLinkContentToClassroom && 'min-h-0 flex-1 pb-0 mobile-lg:w-[450px] mobile-lg:flex-none mobile-lg:shrink-0 mobile-lg:pb-lg')}>
          <ShareContent title={props.title} enterCode={props.enterCode} shareUrl={props.shareUrl} place={props.trackPlace ?? 'unknown'} contentType={props.contentType} contentId={props.contentId} classroomId={props.classroomId} isOwner={isOwner} data-sentry-element="ShareContent" data-sentry-source-file="ContentShareModal.tsx" />
          {props.contentType === 'QUIZSET' && !props.hideFooter && !canShowLinkContentToClassroom && <Link href={props.shareUrl} target="_blank" className="w-full">
                <Button onClick={() => {
            logger.track('enter_space', {
              locale
            });
            logger.track('enter_play', {
              location: 'shareModal',
              spaceId: props.spaceHashId,
              isAuthor: true
            });
          }} className="w-full">
                  {t('global.enter.button')}
                </Button>
              </Link>}
        </div>
        {canShowLinkContentToClassroom && <LinkContentToClassroomSection contentId={props.contentId} contentType={props.contentType} />}
      </DialogBody>
    </ResponsiveDialog>;
}
export const showShareModal = (payload: ShowShareModalPayload) => {
  // 공유 모달 진입은 이 이벤트 하나로만 센다. 같은 행동을 새 이름으로 또 쌓으면
  // 집계 기준이 갈린다 — 진입점·소유 여부·학급은 prop 으로 얹는다.
  trackEvent('enter_share', {
    location: payload.trackLocation,
    contentId: payload.contentId,
    contentType: payload.contentType,
    quizSetId: payload.contentId,
    ...(payload.trackPlace && {
      place: payload.trackPlace
    }),
    ...(payload.isOwner !== undefined && {
      is_owner: payload.isOwner
    }),
    ...(payload.classroomId && {
      classroom_id: payload.classroomId
    })
  });
  // 호출부가 place 를 따로 주지 않으면 기존 trackLocation 어휘를 그대로 쓴다.
  if (payload.contentType === 'QUIZSET') {
    const {
      trackLocation,
      ...modalProps
    } = payload;
    overlay.open(({
      unmount
    }) => <ContentShareModal {...modalProps} trackPlace={modalProps.trackPlace ?? trackLocation} onClose={unmount} />);
    return;
  }
  const {
    trackLocation,
    ...modalProps
  } = payload;
  overlay.open(({
    unmount
  }) => <ContentShareModal {...modalProps} trackPlace={modalProps.trackPlace ?? trackLocation} onClose={unmount} />);
};